import './BasePackCard.css'
import NumberFlow from '@number-flow/react'
import type { ReactNode } from 'react'
import MdiCheckCircle from '~icons/mdi/check-circle'
import { CreditsIcon } from 'src/components/Membership/MembershipIcon'
import { UIButton } from 'src/components/ui/Button'
import { cx } from 'src/utils/cx'
import { ExtraPackageBoostType, type ExtraPackageBoost } from '../api/types'
import { formatLocalizedNumber } from '../utils/formatLocalizedNumber'
import { CreditBoostChip } from './CreditBoostChip'

export interface BasePackCardProps {
  /** Optional badge above the card body (e.g. "Monthly Offer" pill). */
  topBadge?: ReactNode
  /** Pre-formatted base credits string (e.g. "100,000"). */
  baseCreditsLabel: string
  /** Base credits as number; used to compute the absolute bonus. */
  baseCredits: number
  /** Boosted credits as number — also used as NumberFlow value. */
  boostedCredits: number
  /** Whether the boosted total differs from the base credits. */
  isBoosted: boolean
  /** Boost chips to render. */
  boosts: ExtraPackageBoost[]
  /** Pre-formatted price string (e.g. "$9.99"). */
  priceLabel: string
  /**
   * Active app locale (e.g. `en`, `ja`, `zh`). Used by the plain-text
   * fallback to keep number formatting in sync with the rest of the UI;
   * NumberFlow handles its own localization via its `locales` prop.
   */
  locale: string
  /** When true, show NumberFlow animation; otherwise plain text. */
  animateCredits: boolean
  /** Render disabled style (cannot purchase at current tier). */
  disabled?: boolean
  /** Swap button into success state with check icon. */
  showSuccess?: boolean
  onPurchase?: () => void
}

export function BasePackCard({
  topBadge,
  baseCreditsLabel,
  baseCredits,
  boostedCredits,
  isBoosted,
  boosts,
  priceLabel,
  locale,
  animateCredits,
  disabled,
  showSuccess,
  onPurchase,
}: BasePackCardProps) {
  const combinedPercentage = boosts.reduce((sum, b) => sum + b.percentage, 0)
  const isBoostedGradient = boosts.some(
    b => b.type === ExtraPackageBoostType.Membership,
  )
  const bonusAmount = boostedCredits - baseCredits

  return (
    <div
      className={cx(
        'relative w-full min-w-28 xs:min-w-40 xs:max-w-48 md:min-w-60 md:w-60 h-40 md:h-52 flex flex-col bg-[var(--credit-card-background)] dark:bg-[var(--credit-card-background-dark)] rounded-2xl ring-1 ring-transparent font-inter hover:shadow-lg transition cursor-pointer',
        topBadge ? 'mt-6' : 'mt-4',
      )}
      onClick={onPurchase}
    >
      <div className="absolute inset-0 rounded-2xl border border-[var(--credit-card-border)] dark:border-[var(--credit-card-border-dark)] pointer-events-none" />
      {topBadge && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
          {topBadge}
        </div>
      )}
      <div
        className={cx(
          'flex-1 px-3 md:px-4 pb-3 flex flex-col items-center justify-center gap-1.5',
          topBadge ? 'pt-7 md:pt-8' : 'pt-5 md:pt-6',
        )}
      >
        {isBoosted && (
          <CreditBoostChip
            percentage={combinedPercentage}
            isBoostedGradient={isBoostedGradient}
          />
        )}

        <div className="flex items-center gap-1.5">
          <CreditsIcon className="text-dspurple-400 size-5 md:size-6 shrink-0" />
          <span className="text-2xl md:text-3xl font-bold leading-none text-neutral-900 dark:text-neutral-50">
            {animateCredits ? (
              <NumberFlow value={boostedCredits} />
            ) : (
              formatLocalizedNumber(boostedCredits, locale)
            )}
          </span>
        </div>

        {isBoosted && (
          <div className="text-sm md:text-base flex items-baseline gap-1.5">
            <span className="text-neutral-500 dark:text-neutral-400">
              {baseCreditsLabel}
            </span>
            {bonusAmount > 0 && (
              <span className="text-[--bonus-green] font-semibold">
                +{formatLocalizedNumber(bonusAmount, locale)}
              </span>
            )}
          </div>
        )}
      </div>
      <UIButton
        className={cx(
          'h-8 md:h-12 flex items-center z-10 justify-center text-xs md:text-lg font-bold transition-all duration-200 rounded-t-none rounded-b-2xl',
          disabled
            ? 'bg-black/15 text-black/50 hover:bg-black/15 dark:bg-white/15 dark:text-white/50 dark:hover:bg-white/15'
            : showSuccess
              ? 'bg-green-700'
              : 'bg-dspurple-500 hover:bg-dspurple-500/90 dark:bg-dspurple-500 dark:hover:bg-dspurple-500',
        )}
        labelClassName="dark:text-white"
        variant="filled"
        onPress={onPurchase}
      >
        {showSuccess ? (
          <MdiCheckCircle className="size-5 inset-0 m-auto text-white animate-[scale-up_300ms_cubic-bezier(0.34,1.56,0.64,1)]" />
        ) : (
          priceLabel
        )}
      </UIButton>
    </div>
  )
}
